import { Button, Card, Tag } from "@obsina/design-system";
import { Check, Copy } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent } from "react";
import type { AuthenticatedAccount } from "../App";
import { AppShell } from "../AppShell";
import { enMessages } from "../messages";
import type { AppMessages } from "../messages";
import type { AppView } from "../navigation";
import { integrationsMessages, mcpClientRecipes } from "./messages";
import type { McpClientId } from "./messages";
import { readMcpEndpoint } from "./mcp-endpoint";
import type { McpEndpoint } from "./mcp-endpoint";
import "./integrations.css";

export interface IntegrationsViewProps {
  account?: AuthenticatedAccount | null;
  apiBase: string | null;
  messages?: AppMessages;
  activeView?: AppView;
  onNavigate?: (view: AppView) => void;
  onSignOut?: () => Promise<void> | void;
  /**
   * Deployment configuration override. Production reads `import.meta.env`; the
   * tests pass both states explicitly so the published and unpublished
   * surfaces are each covered.
   */
  endpoint?: McpEndpoint;
}

type CopyState = "idle" | "copied" | "failed";

const COPY_RESET_MS = 4000;

/**
 * The assistant-connection surface: what Obsina opens up to an external
 * assistant, and the one command (or one setting) each client needs.
 *
 * The endpoint is deliberately not assumed to exist. Until a deployment is
 * built with `VITE_OBSINA_MCP_URL`, the address is replaced by a placeholder,
 * every copy control is gone rather than disabled, and the surface says plainly
 * that there is nothing to connect to yet.
 */
export function IntegrationsView({
  account = null,
  messages = enMessages,
  activeView = "integrations",
  onNavigate,
  onSignOut,
  endpoint,
}: IntegrationsViewProps) {
  const resolved = useMemo(
    () => endpoint ?? readMcpEndpoint(import.meta.env),
    [endpoint],
  );
  const copy = integrationsMessages;
  const connectHeadingId = useId();
  const exposesHeadingId = useId();
  const [selected, setSelected] = useState<McpClientId>(mcpClientRecipes[0].id);
  const tabRefs = useRef(new Map<McpClientId, HTMLButtonElement>());
  const tabId = useId();
  const panelId = useId();

  const address = resolved.published
    ? resolved.url
    : copy.connect.endpointPlaceholder;
  const recipe =
    mcpClientRecipes.find((entry) => entry.id === selected) ??
    mcpClientRecipes[0];
  const commands = recipe.commands(address);

  const moveSelection = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const keys: Record<string, number> = {
        ArrowLeft: -1,
        ArrowRight: 1,
      };
      const current = mcpClientRecipes.findIndex(
        (entry) => entry.id === selected,
      );
      let nextIndex: number | null = null;
      if (event.key in keys) {
        const step = keys[event.key];
        nextIndex =
          (current + step + mcpClientRecipes.length) % mcpClientRecipes.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = mcpClientRecipes.length - 1;
      }
      if (nextIndex === null) return;
      event.preventDefault();
      const next = mcpClientRecipes[nextIndex];
      setSelected(next.id);
      tabRefs.current.get(next.id)?.focus();
    },
    [selected],
  );

  // AppShell's slot is synchronous; the handler a page passes may return a
  // promise. Discarding it here keeps the shell's contract intact.
  const signOut = onSignOut
    ? () => {
        void onSignOut();
      }
    : undefined;

  return (
    <AppShell
      account={account}
      activeView={activeView}
      messages={messages}
      onNavigate={onNavigate}
      onSignOut={signOut}
    >
      <section className="page-stack mcp-view">
        <header className="page-intro">
          <h1>{copy.title}</h1>
          <p>{copy.lede}</p>
        </header>

        <div className="mcp-layout">
          <Card
            aria-labelledby={connectHeadingId}
            as="section"
            className="mcp-connect"
          >
            <h2 className="mcp-heading" id={connectHeadingId}>
              {copy.connect.title}
            </h2>

            <div className="mcp-endpoint">
              <span className="mcp-endpoint__label">
                {copy.connect.endpointLabel}
              </span>
              <code className="mcp-endpoint__value">{address}</code>
              {resolved.published ? (
                <CopyButton
                  copiedLabel={copy.connect.copiedEndpoint}
                  failedLabel={copy.connect.copyFailed}
                  label={copy.connect.copyEndpoint}
                  value={resolved.url}
                />
              ) : (
                <Tag tone="warning">{copy.unavailable.tag}</Tag>
              )}
            </div>
            {!resolved.published && (
              <p className="mcp-unavailable">{copy.unavailable.body}</p>
            )}

            <div
              aria-label={copy.connect.tablistLabel}
              className="mcp-tabs"
              onKeyDown={moveSelection}
              role="tablist"
            >
              {mcpClientRecipes.map((entry) => (
                <button
                  aria-controls={`${panelId}-${entry.id}`}
                  aria-selected={entry.id === selected}
                  className="mcp-tab"
                  id={`${tabId}-${entry.id}`}
                  key={entry.id}
                  onClick={() => setSelected(entry.id)}
                  ref={(node) => {
                    if (node) tabRefs.current.set(entry.id, node);
                    else tabRefs.current.delete(entry.id);
                  }}
                  role="tab"
                  tabIndex={entry.id === selected ? 0 : -1}
                  type="button"
                >
                  {entry.tab}
                </button>
              ))}
            </div>

            <div
              aria-labelledby={`${tabId}-${recipe.id}`}
              className="mcp-panel"
              id={`${panelId}-${recipe.id}`}
              role="tabpanel"
              tabIndex={0}
            >
              {commands.length > 0 && (
                <div className="mcp-command">
                  <pre className="mcp-command__text">
                    <code>{commands.join("\n")}</code>
                  </pre>
                  {resolved.published && (
                    <CopyButton
                      copiedLabel={copy.connect.copiedCommand}
                      failedLabel={copy.connect.copyFailed}
                      label={copy.connect.copyCommand}
                      value={commands.join("\n")}
                    />
                  )}
                </div>
              )}
              <ol className="mcp-steps">
                {recipe.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </div>

            <p className="mcp-foot">{copy.connect.noInstallLink}</p>
          </Card>

          <section aria-labelledby={exposesHeadingId} className="mcp-exposes">
            <h2 className="mcp-heading" id={exposesHeadingId}>
              {copy.exposes.title}
            </h2>
            <dl className="mcp-tools">
              {copy.exposes.tools.map((tool) => (
                <div className="mcp-tool" key={tool.name}>
                  <dt>
                    <code>{tool.name}</code>
                  </dt>
                  <dd>{tool.body}</dd>
                </div>
              ))}
            </dl>
            <p className="mcp-scope">{copy.exposes.scopeNote}</p>
          </section>
        </div>
      </section>
    </AppShell>
  );
}

/**
 * Copy control with a text result. The outcome is announced and rendered as
 * words, never as colour alone, and a failed clipboard write says so instead of
 * pretending the copy happened.
 */
function CopyButton({
  copiedLabel,
  failedLabel,
  label,
  value,
}: {
  copiedLabel: string;
  failedLabel: string;
  label: string;
  value: string;
}) {
  const [state, setState] = useState<CopyState>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  const run = useCallback(async () => {
    let next: CopyState = "copied";
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      next = "failed";
    }
    setState(next);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState("idle"), COPY_RESET_MS);
  }, [value]);

  return (
    <span className="mcp-copy">
      <Button
        iconStart={
          state === "copied" ? <Check size={14} /> : <Copy size={14} />
        }
        onClick={() => void run()}
        size="sm"
        variant="secondary"
      >
        {label}
      </Button>
      <span className="mcp-copy__status" role="status">
        {state === "copied"
          ? copiedLabel
          : state === "failed"
            ? failedLabel
            : ""}
      </span>
    </span>
  );
}
